import { useState } from "react";
import { toast } from "sonner";
import { submitForm } from "@/lib/forms";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const SPECIALTIES = [
  "Ophthalmology",
  "Optometry",
  "Cornea / External Disease",
  "Refractive Surgery",
  "Retina",
  "Glaucoma",
  "Other",
];

const STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA",
  "KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
  "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT",
  "VA","WA","WV","WI","WY","DC"
];

export default function LeadForm({ source = "homepage", variant = "dark" }) {
  const [form, setForm] = useState({
    name: "",
    practice_name: "",
    specialty: "",
    email: "",
    phone: "",
    state: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.practice_name || !form.specialty || !form.phone || !form.state) {
      toast.error("Please complete the required fields.");
      return;
    }
    setSubmitting(true);
    try {
      const [firstname, ...rest] = (form.name || "").trim().split(/\s+/);
      const lastname = rest.join(" ");
      await submitForm("provider", {
        firstname,
        lastname,
        name: form.name,
        practice_name: form.practice_name,
        specialty: form.specialty,
        email: form.email,
        phone: form.phone,
        state: form.state,
        message: form.message,
        lead_type: "Provider",
        source_page: source,
        lifecyclestage: "lead",
      });
      toast.success("Request received. A RenVia specialist will be in touch within 1 business day.");
      setDone(true);
      setForm({ name: "", practice_name: "", specialty: "", email: "", phone: "", state: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again or email info@renviahealth.com.");
    } finally {
      setSubmitting(false);
    }
  };

  const isDark = variant === "dark";
  const inputCls = isDark
    ? "w-full bg-white/5 border border-white/15 text-white placeholder:text-white/40 rounded-xl px-4 py-3.5 text-sm focus:outline-none focus:border-teal focus:ring-2 focus:ring-teal/20 transition"
    : "w-full bg-cloud border border-transparent text-slate1 placeholder:text-slate1/40 rounded-xl px-4 py-3.5 text-sm focus:outline-none focus:border-teal focus:ring-2 focus:ring-teal/20 transition";
  const labelCls = isDark ? "block text-xs uppercase tracking-[0.18em] text-white/60 mb-2 font-semibold" : "block text-xs uppercase tracking-[0.18em] text-slate1/55 mb-2 font-semibold";

  // Shadcn Select styled to match form (h-auto, larger padding, themed)
  const selectTriggerCls = isDark
    ? "w-full h-auto bg-white/5 border border-white/15 text-white data-[placeholder]:text-white/40 rounded-xl px-4 py-3.5 text-sm shadow-none focus:border-teal focus:ring-2 focus:ring-teal/20 hover:border-white/25 transition"
    : "w-full h-auto bg-cloud border border-transparent text-slate1 data-[placeholder]:text-slate1/40 rounded-xl px-4 py-3.5 text-sm shadow-none focus:border-teal focus:ring-2 focus:ring-teal/20 hover:border-teal/30 transition";
  const selectContentCls = isDark
    ? "bg-slate1 border border-white/15 text-white"
    : "bg-white border border-slate1/10 text-slate1";
  const selectItemCls = isDark
    ? "text-white focus:bg-teal/20 focus:text-white data-[state=checked]:bg-teal/30 data-[state=checked]:text-white cursor-pointer"
    : "text-slate1 focus:bg-cloud focus:text-slate1 data-[state=checked]:bg-teal/10 data-[state=checked]:text-navy cursor-pointer";

  if (done) {
    return (
      <div
        data-testid="lead-form-success"
        className={`rounded-3xl p-10 text-center ${isDark ? "glass-dark text-white" : "bg-white text-slate1 shadow-[0_20px_60px_-20px_rgba(0,45,114,0.25)]"}`}
      >
        <div className="mx-auto h-14 w-14 rounded-full bg-teal/20 flex items-center justify-center mb-4">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#15B8B8" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
        </div>
        <h3 className="font-display text-2xl font-semibold mb-2">Thank you.</h3>
        <p className={isDark ? "text-white/70" : "text-slate1/70"}>
          A RenVia clinical specialist will reach out within one business day to coordinate next steps.
        </p>
        <button
          data-testid="lead-form-reset"
          onClick={() => setDone(false)}
          className="mt-6 text-sm text-teal hover:text-teal2 underline-offset-4 hover:underline"
        >
          Submit another inquiry
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      data-testid="lead-form"
      className={`rounded-3xl p-8 md:p-10 ${isDark ? "glass-dark" : "bg-white shadow-[0_20px_60px_-20px_rgba(0,45,114,0.25)]"}`}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <label className={labelCls}>Full Name *</label>
          <input data-testid="lead-name" value={form.name} onChange={set("name")} className={inputCls} placeholder="Dr. Jane Smith" />
        </div>
        <div>
          <label className={labelCls}>Practice Name *</label>
          <input data-testid="lead-practice" value={form.practice_name} onChange={set("practice_name")} className={inputCls} placeholder="Smith Eye Center" />
        </div>
        <div>
          <label className={labelCls}>Specialty *</label>
          <Select value={form.specialty} onValueChange={(v) => setForm({ ...form, specialty: v })}>
            <SelectTrigger data-testid="lead-specialty" className={selectTriggerCls}>
              <SelectValue placeholder="Select specialty" />
            </SelectTrigger>
            <SelectContent className={selectContentCls}>
              {SPECIALTIES.map((s) => (
                <SelectItem key={s} value={s} className={selectItemCls}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className={labelCls}>Email *</label>
          <input data-testid="lead-email" type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="jane@practice.com" />
        </div>
        <div>
          <label className={labelCls}>Phone *</label>
          <input data-testid="lead-phone" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="(555) 123-4567" />
        </div>
        <div>
          <label className={labelCls}>State *</label>
          <Select value={form.state} onValueChange={(v) => setForm({ ...form, state: v })}>
            <SelectTrigger data-testid="lead-state" className={selectTriggerCls}>
              <SelectValue placeholder="Select state" />
            </SelectTrigger>
            <SelectContent className={`${selectContentCls} max-h-72`}>
              {STATES.map((s) => (
                <SelectItem key={s} value={s} className={selectItemCls}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2">
          <label className={labelCls}>How can we help? (optional)</label>
          <textarea
            data-testid="lead-message"
            value={form.message}
            onChange={set("message")}
            rows={3}
            placeholder="Tell us about your patient population, current ocular surface protocols, or evaluation interest."
            className={inputCls}
          />
        </div>
      </div>

      <div className="mt-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <p className={`text-xs ${isDark ? "text-white/55" : "text-slate1/55"}`}>
          We respect your time. No spam. We only share information about XcellerEYES.
        </p>
        <button
          type="submit"
          disabled={submitting}
          data-testid="lead-submit"
          className="inline-flex items-center justify-center gap-2 rounded-full bg-navy text-white px-8 py-3.5 text-sm font-semibold hover:bg-teal transition-colors shadow-[0_14px_36px_-12px_rgba(0,45,114,0.55)] disabled:opacity-60"
        >
          {submitting ? "Submitting..." : "Request Information"}
          <span aria-hidden>→</span>
        </button>
      </div>
    </form>
  );
}
